<template>
    <component
        :is="tag"
        :class="classTitle"
        class="th-title"
    >
        <slot name="default"></slot>
    </component>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import type { Colors } from '@/components/ui'
import type {
    HorizontalAlignment,
    HtmlTag,
    TitleSize,
    TitleStyle,
} from '@/graphql/api'

interface Props {
    alignment?: HorizontalAlignment
    tag?: HtmlTag
    size?: TitleSize
    titleStyle?: TitleStyle
    color?: Colors
    width?: 'NORMAL' | 'WIDE'
}

const props = withDefaults(defineProps<Props>(), {
    alignment: 'LEFT',
    tag: 'H1',
    size: 'MEDIUM',
    titleStyle: 'PRIMARY',
    color: undefined,
    width: 'NORMAL',
})

const classTitle = computed(() => ({
    [props.alignment?.toLocaleLowerCase()]: true,
    [props.size?.toLocaleLowerCase().replace('_', '-')]: true,
    [props.width?.toLocaleLowerCase()]: true,
    [props.titleStyle?.toLocaleLowerCase()]: true,
}))
</script>

<style lang="scss" scoped>
.th-title {
    width: 100%;
    max-width: 680px;
    margin: 0 auto 20px;
    font-size: 2.25rem;
    display: block;
    color: v-bind(color);
    position: relative;
    z-index: 1;

    &.primary {
        font-family: $parcel-font-stack;
        line-height: 1.4em;
        letter-spacing: 0.01em;

        &.extra-large {
            line-height: 1em;
        }
    }

    &.secondary {
        font-family: $sectra-font-stack;
        line-height: 1.25em;

        &.extra-large {
            line-height: 1em;
        }
    }

    &.primary,
    &.secondary {
        :slotted(.th-ampersand) {
            @include th-ampersand;
        }
    }

    &.tertiary {
        font-family: $effra-font-stack;
        text-transform: uppercase;
        font-weight: 400;
        line-height: 1.25em;
        letter-spacing: 0.05em;
    }

    &.tiny {
        margin: 0 auto 10px;
        font-size: 0.875rem;
    }

    &.small {
        margin: 0 auto 10px;
        font-size: 1.5rem;
    }

    &.medium {
        font-size: 1.5rem;

        @include min-width(768) {
            font-size: 2rem;
        }
    }

    &.large {
        font-size: 2rem;

        @include min-width(768) {
            font-size: 2.25rem;
        }
    }

    &.extra-large {
        font-size: 2.5rem;

        @include min-width(768) {
            font-size: 5rem;
        }

        :slotted(.th-ampersand) {
            &::before {
                top: -0.2em;
                left: -0.25em;
            }
        }
    }

    &.center {
        text-align: center;
    }

    &.left {
        text-align: left;
    }

    &.right {
        text-align: right;
    }

    &.wide {
        max-width: 1004px;
    }
}
</style>
